// 給与スライダー制御ヘルパー

export const SalaryConstants = {
    DEFAULT_MIN: 0,
    DEFAULT_MAX: 100000,
    ALL_TYPE_ID: '0',
};

/**
 * 選択中の給与種別の統計情報がなければスライダー・入力欄を無効化
 * @param {string} selectedTypeId
 * @param {object} salaryStatistics
 * @returns {boolean}
 */
export function isSliderDisabled(selectedTypeId, salaryStatistics) {
    return (
        selectedTypeId === SalaryConstants.ALL_TYPE_ID ||
        !salaryStatistics?.salary_type_ids?.[selectedTypeId]
    );
}

/**
 * 給与値を正規化するヘルパー関数
 * @param {string|number} min - 最小値
 * @param {string|number} max - 最大値
 * @returns {Object} 正規化された値
 */
export function normalizeSalaryValues(min, max) {
    const minValue = min ? parseFloat(min.toString().replace(/,/g, '')) : null;
    const maxValue = max ? parseFloat(max.toString().replace(/,/g, '')) : null;
    
    // minがmaxより大きい場合は入れ替え
    if (minValue !== null && maxValue !== null && minValue > maxValue) {
        return { min: maxValue, max: minValue };
    }
    
    return { min: minValue, max: maxValue };
}

/**
 * 給与フィルターの送信判定（スライダー範囲の端の値は送信しない）
 * @param {Object} params - パラメータ
 * @param {string} params.typeId - 給与種別ID
 * @param {string|number} params.min - 最小値
 * @param {string|number} params.max - 最大値
 * @param {Object} params.salaryStatistics - 給与統計データ
 * @returns {Object} フィルター送信判定結果
 */
export function getSendConditions({ typeId, min, max, salaryStatistics }) {
    // 給与種別が「全て」の場合は送信しない
    if (typeId === SalaryConstants.ALL_TYPE_ID) {
        return { shouldSendMin: false, shouldSendMax: false, isAll: true };
    }
    
    const maxStatValue = salaryStatistics?.salary_type_ids?.[typeId]?.max;
    
    return {
        // 最小値が0以下（スライダー最小値）の場合は送信しない
        shouldSendMin: min && Number(min) > 0,
        // 最大値が統計最大値以上（スライダー最大値）の場合は送信しない
        shouldSendMax: max && maxStatValue != null && Number(max) < Number(maxStatValue),
        isAll: false
    };
}

/**
 * 統計データから範囲を取得
 * @param {Object} statistics - 統計データ
 * @param {string} typeId - 給与種別ID
 * @param {number} defaultMin - デフォルト最小値
 * @param {number} defaultMax - デフォルト最大値
 * @returns {Object} 範囲情報
 */
export function getRangeFromStatistics(statistics, typeId, defaultMin, defaultMax) {
    // 統計値がある場合は最大値を使用、なければデフォルト値
    const maxValue = statistics?.salary_type_ids?.[typeId]?.max ?? defaultMax;
    
    return {
        rangeMin: defaultMin,
        rangeMax: maxValue,
        sliderStartMin: defaultMin,
        sliderStartMax: maxValue
    };
}

/**
 * 初期値の計算
 * @param {Object} initialValues - 初期値オブジェクト
 * @param {string} typeId - 給与種別ID
 * @param {number} sliderStartMin - スライダー開始最小値
 * @param {number} sliderStartMax - スライダー開始最大値
 * @returns {Object} 計算された初期値
 */
export function getInitialValues(initialValues, typeId, sliderStartMin, sliderStartMax) {
    // 給与種別が変わった場合はスライダーの初期値を使用
    if (!initialValues || initialValues.typeId !== typeId) {
        return { initialMin: sliderStartMin, initialMax: sliderStartMax };
    }

    // 給与種別が変わっていない場合は保存された初期値を使用
    return {
        initialMin: initialValues.min || sliderStartMin,
        initialMax: initialValues.max || sliderStartMax,
    };
} 